import React, {useState} from "react";
import User from "../../model/User";
import {Box, DialogTitle, FormControlLabel, Grid, Link, Paper, Typography, useTheme} from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import FormValidator from "../../components/form/FormValidator";
import CancelButton from "../../components/form/CancelButton";
import ProgressButton from "../../components/form/ProgressButton";
import DialogActions from "@mui/material/DialogActions";
import CheckboxValidator from "../../components/form/CheckboxValidator";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import Review from "../../model/Review";
import Model from "../../model/Model";
import {ConvertKitTag} from "../../apis/ConvertKitAPI";
import {UpdateUserInput, UserStatus, UserType} from "../../API";
import {getISODateToday} from "../../stores/StoreUtilities";
import {useResources} from "../../stores/ResourceProvider";
import useNavigationHelper from "../../components/NavigationHelper";

const DeleteAccountDialog = ({
  user,
  onSubmit
} : {
  user: User
  onSubmit(confirmed: boolean): void
}) => {
  const [reason, setReason] = useState<string>("")
  const [confirmed, setConfirmed] = useState<boolean>(false)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  const { userStore, accountStore, modelStore, authAPI, billingAPI, convertKitAPI, notify } = useResources()
  const {navigateHome} = useNavigationHelper()

  const theme = useTheme()
  const styles = {
    warningBox: {
      borderStyle: "solid",
      borderRadius: "5px",
      borderWidth: "1px 1px 1px 10px",
      borderColor: theme.palette.warning.main,
      padding: theme.spacing(1)
    }
  }

  const handleSubmit = async () => {
    if (confirmed) {
      await deleteUser(user)
    }
  }

  const handleClose = () => {
    onSubmit(false)
  }

  const deleteUser = async (userModel: User) => {
    try {
      const user = await userStore.getUser(userModel.id)
      if (user) {
        setIsProcessing(true)
        // Delete Reviews
        if (user.reviews.length > 0) {
          const reviewPromises: Promise<Review | undefined>[] = []
          user.reviews.forEach((r: any) => {
            reviewPromises.push(userStore.deleteReview(r.id))
          })
          await Promise.all(reviewPromises)
        }

        // Delete models
        const promises: Promise<Model | undefined>[] = []
        user.models.forEach((model: Model) => {
          promises.push(modelStore.deleteModel(model.id, true))
        })
        await Promise.all(promises)

        await convertKitAPI.addTag(ConvertKitTag.Cancellation, user.email)

        // Cancel Stripe subscription
        if (user.userType === UserType.Premium && user.customerId) {
          const customer = await billingAPI.getCustomer(user.customerId)
          if (customer && customer.subscriptions && customer.subscriptions.data && customer.subscriptions.data.length > 0) {
            const subscriptionId = customer.subscriptions.data[0].id
            const result = await billingAPI.cancelSubscription(subscriptionId)
            console.log(`cancelSubscription result`, result)
          }
        }

        // Mark user deleted
        const update: UpdateUserInput = {
          id: user.id,
          userStatus: UserStatus.Deleted,
          deletedOn: getISODateToday(),
          deletedReason: reason
        }
        const deleted = await userStore.updateUser(update)
        if (deleted) {
          // Delete cognito user
          const result = await authAPI.deleteUser(user.id)
          console.log(`deleteUser result:`, result)
        }

        if (deleted) {
          await userStore.signOut()
          navigateHome()
        }
      }
    } catch (err: any) {
      notify.show("error", err.message)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Dialog
      id="DeleteAccountDialog"
      open={true}
      onClose={() => {}}
      scroll="paper"
      maxWidth="sm"
      fullWidth
      aria-labelledby="DeletteAccountDialogTitle"
    >
      <Paper sx={{width:"100%"}}>
        <FormValidator onSubmit={handleSubmit} autoComplete="off">
          <DialogTitle id="DeletteAccountDialogTitle">
            Close My Account
          </DialogTitle>
          <DialogContent sx={{m:0, marginTop:2}}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Typography variant="body2">
                  We're sorry to see you go! Before permanently deleting your account, please <Link href="mailto:support@retirementbudgetcalculator.com?subject=Close%20My%20Account">Contact Our Support Team</Link> if you have any questions or need assistance.
                </Typography>
              </Grid>
              <Grid item>
                <Box sx={styles.warningBox}>
                  Please note, account closure is a permanent action and your data cannot be restored.
                  If you decide later that you want to use RBC again, you will need to create a new account.
                </Box>
              </Grid>
            </Grid>
            <Grid item sx={{mt:1}}>
              <TextFieldValidator
                margin="dense"
                name="reason"
                label="Reason for closing your account (optional)"
                type="text"
                variant="outlined"
                multiline={true}
                rows={2}
                validators={{required:false}}
                onChange={(event: any) => setReason(event.target.value)}
                value={reason}
                placeholder=""
                fullWidth
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                sx={{ px: 2, py: 1 }}
                control={
                  <CheckboxValidator
                    name="termsCheckbox"
                    color="primary"
                    checked={confirmed}
                    value="checkedG"
                    onChange={(event: any) => {
                      setConfirmed(event.target.checked)
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ pl: 0 }}>
                    Yes, I want to permanently close my Retirement Budget Calculator account and delete my data.
                  </Typography>
                }
              />
            </Grid>
            <Grid item>
              <Typography variant="body2" sx={{fontSize:14}}>
                Please be advised that RBC is legally required or entitled to retain some types of data, such as subscription
                history. We do this in line with applicable laws including for tax and accounting and fraud prevention purposes.
              </Typography>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Box display="flex" justifyContent="flex-end">
              <CancelButton onClick={handleClose} disabled={isProcessing}/>
              <ProgressButton variant="contained" size="medium" color="primary" sx={{ color: "#fff" }}
                              type="submit" processing={isProcessing} disabled={!confirmed}>
                Close My Account
              </ProgressButton>
            </Box>
          </DialogActions>
        </FormValidator>
      </Paper>
    </Dialog>
  )
}

export default DeleteAccountDialog